import {createSelector} from 'reselect'
import keyBy from 'lodash/keyBy.js'
import get from 'lodash/get.js'
import sortBy from 'lodash/sortBy.js'

import {getState, setForm, formsSelector} from '../store.js'
import apiverson from '../common/apiverson.js'
import {hasStocktakePermissionSelector} from '../data/me.js'
import {
  hasUseStocktakeFeatureSelector,
  showFeatureLocksSelector,
} from '../data/company.js'
import {warehousesSelector} from './warehouses.js'
import {dateTimeFormat} from '../common/date.js'
import {
  STOCKTAKE_CANCELLED_DISPLAY,
  STOCKTAKE_COMPLETED_DISPLAY,
  STOCKTAKE_INITIATED_DISPLAY,
  STOCKTAKE_LINE_COUNTED,
  STOCKTAKE_LINE_RECONCILED,
  STOCKTAKE_SKU_STATUS_BY_VALUE,
  STOCKTAKE_STARTED_DISPLAY,
  STOCKTAKE_TROOLEAN_FILTER_OPTIONS,
} from '../common/constants/Stocktakes.js'
import {tokenizeOptions} from '../common/tokenizeOptions.js'
import {buildPath} from '../common/querystring.js'
import {fetchAllApiverson} from '../common/fetchAll.js'

export const STOCKTAKES = 'stocktakes'

export function setStocktakes(stocktakes) {
  setForm(STOCKTAKES, keyBy(stocktakes, 'reference_id'))
}

export function setStocktake(stocktake) {
  const stocktakes = stocktakesSelector(getState())

  setForm(STOCKTAKES, {...stocktakes, [stocktake.reference_id]: stocktake})
}

export function updateStocktake(referenceID, updates) {
  const stocktake = stocktakeSelector(getState(), {referenceID})

  setStocktake({...stocktake, ...updates})
}

export function removeStocktake(referenceID) {
  const stocktakes = {...stocktakesSelector(getState())}

  delete stocktakes[referenceID]

  setForm(STOCKTAKES, stocktakes)
}

export function stocktakesSelector(state) {
  return formsSelector(state)[STOCKTAKES] || stocktakesSelector.default
}
stocktakesSelector.default = {}

export function stocktakeSelector(state, {referenceID}) {
  return stocktakesSelector(state)[referenceID]
}

export function stocktakesByReferenceIDSelector(state, {referenceIDs}) {
  const stocktakes = stocktakesSelector(state)

  return referenceIDs.map((referenceID) => stocktakes[referenceID])
}

export const createStocktakeSelector = (referenceID) =>
  createSelector(stocktakesSelector, (stocktakes) => stocktakes[referenceID])

export function hasStocktakeFeatureSelector(state) {
  return hasUseStocktakeFeatureSelector(state)
}

export const canUseStocktakeSelector = createSelector(
  hasStocktakePermissionSelector,
  hasStocktakeFeatureSelector,
  (hasPermission, hasFeature) => hasPermission && hasFeature,
)

export function stocktakeFilterOptionsSelector() {
  return STOCKTAKE_TROOLEAN_FILTER_OPTIONS
}

export const stocktakeFilterOptionTokensSelector = createSelector(
  stocktakeFilterOptionsSelector,
  (stocktakeFilterOptions) => tokenizeOptions(stocktakeFilterOptions),
)

export function showStocktakeNavSelector(state) {
  const showFeatureLocks = showFeatureLocksSelector(state)
  const hasStocktakeFeature = hasStocktakeFeatureSelector(state)
  const hasStocktakePermission = hasStocktakePermissionSelector(state)

  return hasStocktakePermission && (hasStocktakeFeature || showFeatureLocks)
}

export function stocktakeStatusSelector(state, {referenceID}) {
  const stocktake = stocktakeSelector(state, {referenceID})

  return stocktake.cancelled_date
    ? STOCKTAKE_CANCELLED_DISPLAY
    : stocktake.completed_date
      ? STOCKTAKE_COMPLETED_DISPLAY
      : stocktake.started_date
        ? STOCKTAKE_STARTED_DISPLAY
        : STOCKTAKE_INITIATED_DISPLAY
}

export function stocktakeLinesSelector(state, {referenceID}) {
  const stocktake = stocktakeSelector(state, {referenceID})

  return get(stocktake, 'lines', [])
}

export function stocktakeLineSelector(state, {referenceID, lineID}) {
  const lines = stocktakeLinesSelector(state, {referenceID})

  return lines.find(({id}) => id === lineID)
}

export function commentsSelector(state, {referenceID}) {
  const stocktake = stocktakeSelector(state, {referenceID})

  return get(stocktake, 'comments', [])
}

export function getCommentGroups(stocktake) {
  const comments = get(stocktake, 'comments', [])
  const daysIndex = {}

  return sortBy(comments, 'date')
    .reverse()
    .reduce((days, comment) => {
      const day = dateTimeFormat(comment.date)

      if (daysIndex[day]) {
        daysIndex[day].comments.push(comment)
      } else {
        daysIndex[day] = {
          day,
          comments: [comment],
        }

        days.push(daysIndex[day])
      }

      return days
    }, [])
}

export const createStocktakeCommentGroupsSelector = (stocktakeSelector) =>
  createSelector(stocktakeSelector, (stocktake) => getCommentGroups(stocktake))

function getWarehouseID(stocktake) {
  return get(stocktake, 'warehouse_id', null)
}

export function stocktakeWarehouseIDSelector(state, {referenceID}) {
  const stocktake = stocktakeSelector(state, {referenceID})

  return getWarehouseID(stocktake)
}

export const createStocktakeLinesSelector = (stocktakeSelector) =>
  createSelector(stocktakeSelector, (stocktake) => {
    return get(stocktake, 'lines', [])
  })

export const createStocktakeTotalSKUCountSelector = (stocktakeSelector) =>
  createSelector(stocktakeSelector, (stocktake) => {
    const lines = get(stocktake, 'lines', [])

    return Object.keys(
      lines.reduce((prev, {sku}) => {
        prev[sku] = true

        return prev
      }, {}),
    ).length
  })

export const createStocktakeTotalLineQuantitySelector = (stocktakeSelector) =>
  createSelector(stocktakeSelector, (stocktake) => {
    const lines = get(stocktake, 'lines', [])

    return lines.reduce(
      (prev, {requested_quantity}) => prev + requested_quantity,
      0,
    )
  })

export const createStocktakeTotalFinishedSelector = (stocktakeSelector) =>
  createSelector(stocktakeSelector, (stocktake) => {
    const lines = get(stocktake, 'lines', [])

    return lines.reduce(
      (prev, {finished_quantity}) => prev + finished_quantity,
      0,
    )
  })

export function internalNotesSelector(state, {referenceID}) {
  const stocktake = stocktakeSelector(state, {referenceID})

  return get(stocktake, 'internal_notes')
}

export function lineCountSelector(state, {referenceID}) {
  const stocktake = stocktakeSelector(state, {referenceID})

  return get(stocktake, 'line_count', lineCountSelector.default)
}
lineCountSelector.default = {}

export function lineCountListSelector(state, {referenceID}) {
  const lineCount = lineCountSelector(state, {referenceID})

  return Object.entries(lineCount)
    .filter(([status]) =>
      [STOCKTAKE_LINE_COUNTED, STOCKTAKE_LINE_RECONCILED].includes(status),
    )
    .map(([status, count]) => ({
      ...STOCKTAKE_SKU_STATUS_BY_VALUE[status],
      count,
    }))
}

export function canStocktakeLabelSelector(state, {referenceID}) {
  const lineCount = lineCountSelector(state, {referenceID})
  const status = stocktakeStatusSelector(state, {referenceID})

  return [STOCKTAKE_CANCELLED_DISPLAY, STOCKTAKE_COMPLETED_DISPLAY].includes(
    status,
  )
    ? ''
    : lineCount[STOCKTAKE_LINE_COUNTED] === 0
      ? 'Start Stocktake'
      : 'Continue Stocktake'
}

export const createStocktakeWarehouseSelector = (stocktakeSelector) =>
  createSelector(
    stocktakeSelector,
    warehousesSelector,
    (stocktake, warehouses) => {
      const warehouseID = get(stocktake, 'warehouse_id')

      return get(warehouses, warehouseID, {id: warehouseID, address: {}})
    },
  )

export const createStocktakeCreatedDateSelector = (stocktakeSelector) =>
  createSelector(stocktakeSelector, (stocktake) =>
    get(stocktake, 'created_date', ''),
  )

export const createStocktakeUpdatedDateSelector = (stocktakeSelector) =>
  createSelector(stocktakeSelector, (stocktake) =>
    get(stocktake, 'updated_date', ''),
  )

export const createStocktakeTagsSelector = (stocktakeSelector) =>
  createSelector(stocktakeSelector, (stocktake) => get(stocktake, 'tags', []))

export function patchStocktakeTag(
  referenceID,
  stocktakeTag,
  needsAdding = true,
) {
  const stocktake = stocktakeSelector(getState(), {referenceID})

  if (!stocktake) {
    return
  }

  const tags = stocktake.tags.filter(({id}) => id !== stocktakeTag.id)

  if (needsAdding) {
    tags.push(stocktakeTag)
  }

  setStocktake({...stocktake, tags})
}

export async function ensureStocktake(referenceID, {reload} = {}) {
  let stocktake = stocktakeSelector(getState(), {referenceID})

  if (!reload && stocktake) {
    return stocktake
  }

  const {json} = await apiverson.get(
    `/stocktake/${encodeURIComponent(referenceID)}`,
  )

  setStocktake(json)

  return json
}

export async function ensureStocktakeComments(referenceID) {
  const stocktake = stocktakeSelector(getState(), {referenceID})

  if (stocktake?.comments?.length > 0) {
    return
  }

  const comments = await fetchAllApiverson(
    buildPath(['stocktake', referenceID, 'comment']),
    'comment',
  )

  updateStocktake(referenceID, {comments})
}
